import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import {
  hasQuestionBeenSkipped,
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import {
  StepLayout,
  ButtonFooterLayout,
  ContinueButton,
  SkipButton,
  RoutingFlowCheckboxGroup,
  useConvertTranslatedLabel,
  RoutingFlowTitle
} from "#/components/carrot-plans/shared"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { DoAnyApplyOption, RespondedToSprintsScreener } from "#/components/carrot-plans/types/doAnyApplyOption"
import useAboutYou from "#/components/carrot-plans/hooks/useAboutYou"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useRoutingFlow } from "#/components/carrot-plans/shared/RoutingFlowContainer"
import { useSprintsCandidacyQuery } from "#/services/user/hooks/useSprintsQuery"
import { Journey } from "#/components/carrot-plans/types/journey"
import {
  CarrotPlanRequestType,
  useGetInProgressCarrotPlanRequestType
} from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"

type FormValues = {
  doAnyApply?: string[]
  respondedToMemberSprintsScreener?: string
  partnerDoAnyApply?: string[]
  respondedToPartnerSprintsScreener?: string
}

export const metabolicHealthOptions = Object.entries(
  defineMessages({
    [DoAnyApplyOption.OBESITY]: { defaultMessage: "Obesity" },
    [DoAnyApplyOption.OVERWEIGHT]: { defaultMessage: "Overweight" },
    [DoAnyApplyOption.DIABETES]: { defaultMessage: "Diabetes, pre-diabetes, or insulin resistance" },
    [DoAnyApplyOption.METABOLIC_SYNDROME]: { defaultMessage: "Metabolic syndrome" },
    [DoAnyApplyOption.HIGH_CHOLESTEROL]: { defaultMessage: "High cholesterol or dyslipidemia " },
    [DoAnyApplyOption.PCOS]: { defaultMessage: "Polycystic ovary syndrome (PCOS)" },
    [DoAnyApplyOption.FATTY_LIVER_DISEASE]: { defaultMessage: "Fatty liver disease" },
    [DoAnyApplyOption.LIPODYSTROPHY]: { defaultMessage: "Lipodystrophy" }
  })
).map(([value, label]) => ({ label, value }))

const noneOfTheAboveOption = Object.entries(
  defineMessages({
    [DoAnyApplyOption.NONE]: { defaultMessage: "None of the above" }
  })
).map(([value, label]) => ({ label, value }))[0]

const generalOptions = Object.entries(
  defineMessages({
    [DoAnyApplyOption.OVER_40]: { defaultMessage: "Over the age of 40" },
    [DoAnyApplyOption.TWO_LOSSES]: { defaultMessage: "Two or more unintended pregnancy losses" },
    [DoAnyApplyOption.URGENT_MEDICAL_NEED]: { defaultMessage: "Urgent medical need for fertility preservation" },
    [DoAnyApplyOption.NEVER_MENSTRUATED]: { defaultMessage: "Never menstruated" },
    [DoAnyApplyOption.FAMILY_BLOOD_CLOTTING]: {
      defaultMessage: "Immediate family member with history of blood clotting disorders"
    }
  })
).map(([value, label]) => ({ label, value }))

const pregnantOptions = Object.entries(
  defineMessages({
    [DoAnyApplyOption.RECURRENT_PREGNANCY_LOSS]: { defaultMessage: "Recurrent pregnancy loss" },
    [DoAnyApplyOption.PREECLAMPSIA_HIGH_BLOOD_PRESSURE]: { defaultMessage: "Preeclampsia or high blood pressure" },
    [DoAnyApplyOption.GESTATIONAL_DIABETES]: { defaultMessage: "Gestational diabetes" },
    [DoAnyApplyOption.METABOLIC_CONDITIONS]: {
      defaultMessage: "Metabolic conditions (e.g., diabetes, polycystic ovary syndrome, overweight, obesity)"
    },
    [DoAnyApplyOption.PRETERM_LABOR_DELIVERY]: { defaultMessage: "Preterm labor or delivery" },
    [DoAnyApplyOption.CESAREAN_BIRTH]: { defaultMessage: "A cesarean birth" },
    [DoAnyApplyOption.PLACENTA_PREVIA_ACCRETA_ABRUPTION]: { defaultMessage: "Placenta previa, accreta, or abruption" },
    [DoAnyApplyOption.OTHER_CONDITION]: {
      defaultMessage:
        "Other medical condition impacting pregnancy (e.g., fertility diagnosis, thyroid disorder, endometriosis, history of blood clotting disorders)"
    },
    [DoAnyApplyOption.OVER_35]: { defaultMessage: "Over the age of 35" }
  })
).map(([value, label]) => ({ label, value }))

const AfabOnlyOptions = [DoAnyApplyOption.NEVER_MENSTRUATED, DoAnyApplyOption.PCOS]

/**
 * Returns the options for the "Do Any Apply" step, always including "None of the above" last.
 * @param isSprintsCandidate - Whether the user is a sprints candidate
 * @param isCheckInFlow - Whether the user is in the check-in flow
 * @param [journey] - The user's journey
 */
export function getDoAnyApplyOptions(isSprintsCandidate: boolean, isCheckInFlow: boolean, journey?: Journey | null) {
  if (journey && journey === Journey.PREGNANT) {
    return [...pregnantOptions, noneOfTheAboveOption]
  }
  if (isSprintsCandidate && isCheckInFlow) {
    return [...metabolicHealthOptions, noneOfTheAboveOption]
  }
  if (isSprintsCandidate) {
    return [...metabolicHealthOptions, ...generalOptions, noneOfTheAboveOption]
  }
  return [...generalOptions, noneOfTheAboveOption]
}

function DoAnyOfTheFollowingApply({
  name,
  header,
  options,
  defaultValue,
  onClickContinue,
  onSkip
}: {
  name: "doAnyApply" | "partnerDoAnyApply"
  header: string
  options: { label: string; value: unknown }[]
  defaultValue: string[]
  onClickContinue: (values: FormValues) => Promise<void>
  onSkip: () => Promise<void>
}): JSX.Element {
  const intl = useIntl()
  const nameLabel = `${name}-label`

  return (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id={nameLabel}>{header}</span>}
        description={intl.formatMessage({
          defaultMessage:
            "Select as many as apply. This information helps us understand how to best support you on your journey."
        })}
        formElements={
          <Form
            defaultValues={{
              [name]: defaultValue
            }}
            onSubmit={onClickContinue}
          >
            <RoutingFlowCheckboxGroup aria-labelledby={nameLabel} name={name} options={options} />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

function getSendValue(
  requestType: CarrotPlanRequestType,
  partnerIsCurrentCandidate: boolean,
  journey: Journey
): "PARTNER" | "" | Journey {
  if (partnerIsCurrentCandidate && journey !== Journey.PREGNANT) return "PARTNER"
  return requestType === CarrotPlanRequestType.CHECK_IN_INTAKE ? "" : journey
}

function getDefaultSendValue(
  defaultValue: string[],
  hasQuestionBeenSkipped: boolean,
  partnerIsCurrentCandidate: boolean,
  journey: Journey,
  requestType: CarrotPlanRequestType
): "PARTNER" | Journey | "" | null {
  if (defaultValue?.length || hasQuestionBeenSkipped) {
    return getSendValue(requestType, partnerIsCurrentCandidate, journey)
  }
  return null
}

function isRespondingToSprintsScreener(isSprintsCandidate: boolean): RespondedToSprintsScreener | null {
  return isSprintsCandidate ? RespondedToSprintsScreener.YES : null
}

export function DoAnyOfTheFollowingApplyStep() {
  const routingData = useCheckRoutingInfoQueryCache()
  const { doAnyApplyData, setDoAnyApplyData } = useRoutingFlow()
  const sprintsCandidacyQuery = useSprintsCandidacyQuery()
  const requestTypeQuery = useGetInProgressCarrotPlanRequestType()
  const isPartnerSprintsCandidate = sprintsCandidacyQuery.data?.partnerIsSprintsCandidate ?? false
  const isMemberSprintsCandidate = sprintsCandidacyQuery.data?.memberIsSprintsCandidate ?? false

  // Use context data if available, otherwise use API data
  const currentDoAnyApply = React.useMemo(() => {
    // If context has data, use it
    if (doAnyApplyData.length > 0) {
      return doAnyApplyData
    }
    // Otherwise, fall back to API data
    return routingData?.doAnyApply || []
  }, [doAnyApplyData, routingData?.doAnyApply])

  const sendValue = getDefaultSendValue(
    currentDoAnyApply,
    hasQuestionBeenSkipped("doAnyApply", routingData),
    isPartnerSprintsCandidate,
    routingData?.journey as Journey,
    requestTypeQuery.data
  )

  return sprintsCandidacyQuery.isFetching || requestTypeQuery.isLoading ? (
    <CenteredLoadingIndicator />
  ) : (
    <DoAnyOfTheFollowingApplyStepInner
      sendValue={sendValue}
      memberIsCurrentCandidate={isMemberSprintsCandidate}
      partnerIsCurrentCandidate={isPartnerSprintsCandidate}
      defaultValue={currentDoAnyApply}
      setLocalDoAnyApply={setDoAnyApplyData}
      journey={routingData?.journey as Journey}
      requestType={requestTypeQuery.data}
    />
  )
}

function DoAnyOfTheFollowingApplyStepInner({
  sendValue,
  memberIsCurrentCandidate,
  partnerIsCurrentCandidate,
  defaultValue,
  setLocalDoAnyApply,
  journey,
  requestType
}: {
  sendValue: "PARTNER" | Journey | "" | null
  memberIsCurrentCandidate: boolean
  partnerIsCurrentCandidate: boolean
  defaultValue: string[]
  setLocalDoAnyApply: React.Dispatch<React.SetStateAction<string[]>>
  journey: Journey
  requestType: CarrotPlanRequestType
}) {
  const intl = useIntl()
  const isCheckInFlow = requestType === CarrotPlanRequestType.CHECK_IN_INTAKE
  const { mutateAsync } = useUpdateRoutingInfo()
  const { firstName, preferredName, sex } = useCurrentUser()
  const { isPartnerAccessingAccount } = useUserRole()
  const { send } = useStateMachine(sendValue)

  const allOptions = getDoAnyApplyOptions(memberIsCurrentCandidate, isCheckInFlow, journey)
  const sexFilteredOptions =
    !partnerIsCurrentCandidate || sex === "FEMALE"
      ? allOptions
      : allOptions.filter((o) => !AfabOnlyOptions.includes(o.value as DoAnyApplyOption))
  const options = useConvertTranslatedLabel(sexFilteredOptions)

  async function onClickContinue(values: FormValues) {
    if (values?.doAnyApply?.length) {
      values.respondedToMemberSprintsScreener = isRespondingToSprintsScreener(memberIsCurrentCandidate)
      await mutateAsync(values, {
        onSuccess: () => {
          const newValue = values.doAnyApply || []
          setLocalDoAnyApply(newValue)
          send(getSendValue(requestType, partnerIsCurrentCandidate, journey))
        }
      })
    } else {
      await onSkip()
    }
  }

  async function onSkip() {
    await mutateAsync(
      { skipFields: ["doAnyApply"] },
      {
        onSuccess: () => {
          setLocalDoAnyApply([])
          send(getSendValue(requestType, partnerIsCurrentCandidate, journey))
        }
      }
    )
  }

  const header = (() => {
    if (journey !== Journey.PREGNANT) {
      if (isPartnerAccessingAccount) {
        return intl.formatMessage(
          {
            defaultMessage: "Do any of the following apply to {memberName}?"
          },
          { memberName: preferredName ?? firstName }
        )
      }

      if (partnerIsCurrentCandidate) {
        return intl.formatMessage({
          defaultMessage: "Do any of the following apply to you?"
        })
      }
    }

    return intl.formatMessage({
      defaultMessage: "Do any of the following apply?"
    })
  })()

  return (
    <DoAnyOfTheFollowingApply
      name="doAnyApply"
      header={header}
      options={options}
      defaultValue={defaultValue || []}
      onClickContinue={onClickContinue}
      onSkip={onSkip}
    />
  )
}

function getPartnerSendValue(requestType: CarrotPlanRequestType | null, journey: Journey): "" | Journey {
  return requestType === CarrotPlanRequestType.CHECK_IN_INTAKE ? "" : journey
}

function getPartnerDefaultSendValue(
  defaultValue: string[],
  hasQuestionBeenSkipped: boolean,
  requestType: CarrotPlanRequestType | null,
  journey: Journey
): Journey | "" | null {
  if (defaultValue?.length || hasQuestionBeenSkipped) {
    return getPartnerSendValue(requestType, journey)
  }
  return null
}

export function PartnerDoAnyOfTheFollowingApplyStep(): JSX.Element {
  const routingData = useCheckRoutingInfoQueryCache()
  const sprintsCandidacyQuery = useSprintsCandidacyQuery()
  const requestTypeQuery = useGetInProgressCarrotPlanRequestType()
  const aboutYouQuery = useAboutYou()
  const { partnerDoAnyApplyData, setPartnerDoAnyApplyData } = useRoutingFlow()
  const partnerIsCurrentCandidate = sprintsCandidacyQuery.data?.partnerIsSprintsCandidate ?? false

  // Use context data if available, otherwise use API data
  const currentPartnerDoAnyApply = React.useMemo(() => {
    // If context has data, use it
    if (partnerDoAnyApplyData.length > 0) {
      return partnerDoAnyApplyData
    }
    // Otherwise, fall back to API data
    return routingData?.partnerDoAnyApply || []
  }, [partnerDoAnyApplyData, routingData?.partnerDoAnyApply])

  const sendValue = getPartnerDefaultSendValue(
    currentPartnerDoAnyApply,
    hasQuestionBeenSkipped("partnerDoAnyApply", routingData),
    requestTypeQuery.data,
    routingData?.journey as Journey
  )

  return aboutYouQuery?.isLoading || sprintsCandidacyQuery.isFetching || requestTypeQuery.isLoading ? (
    <CenteredLoadingIndicator />
  ) : (
    <PartnerDoAnyOfTheFollowingApplyStepInner
      sendValue={sendValue}
      partnerIsCurrentCandidate={partnerIsCurrentCandidate}
      defaultValue={currentPartnerDoAnyApply}
      setLocalPartnerDoAnyApply={setPartnerDoAnyApplyData}
      journey={routingData?.journey as Journey}
      requestType={requestTypeQuery.data}
      partnerFirstName={aboutYouQuery.data?.partnerFirstName}
      partnerSex={aboutYouQuery.data?.partnerSex}
    />
  )
}

function PartnerDoAnyOfTheFollowingApplyStepInner({
  sendValue,
  partnerIsCurrentCandidate,
  defaultValue,
  setLocalPartnerDoAnyApply,
  journey,
  requestType,
  partnerFirstName,
  partnerSex
}: {
  sendValue: Journey | "" | null
  partnerIsCurrentCandidate: boolean
  defaultValue: string[]
  setLocalPartnerDoAnyApply: React.Dispatch<React.SetStateAction<string[]>>
  journey: Journey
  requestType: CarrotPlanRequestType
  partnerFirstName?: string
  partnerSex?: string
}): JSX.Element {
  const intl = useIntl()
  const { mutateAsync } = useUpdateRoutingInfo()
  const { send } = useStateMachine(sendValue)
  const isCheckInFlow = requestType === CarrotPlanRequestType.CHECK_IN_INTAKE

  const allOptions = getDoAnyApplyOptions(partnerIsCurrentCandidate, isCheckInFlow)
  const sexFilteredOptions =
    partnerSex === "FEMALE"
      ? allOptions
      : allOptions.filter((o) => !AfabOnlyOptions.includes(o.value as DoAnyApplyOption))
  const options = useConvertTranslatedLabel(sexFilteredOptions)

  async function onClickContinue(values: FormValues) {
    if (values?.partnerDoAnyApply?.length) {
      values.respondedToPartnerSprintsScreener = isRespondingToSprintsScreener(partnerIsCurrentCandidate)
      await mutateAsync(values, {
        onSuccess: () => {
          const newValue = values.partnerDoAnyApply || []
          setLocalPartnerDoAnyApply(newValue)
          send(getPartnerSendValue(requestType, journey))
        }
      })
    } else {
      await onSkip()
    }
  }

  async function onSkip() {
    await mutateAsync(
      { skipFields: ["partnerDoAnyApply"] },
      {
        onSuccess: () => {
          setLocalPartnerDoAnyApply([])
          send(getPartnerSendValue(requestType, journey))
        }
      }
    )
  }

  const header = intl.formatMessage(
    {
      defaultMessage: "Do any of the following apply to {partnerName}?"
    },
    { partnerName: partnerFirstName ?? "your partner" }
  )

  return (
    <DoAnyOfTheFollowingApply
      name="partnerDoAnyApply"
      header={header}
      options={options}
      defaultValue={defaultValue}
      onClickContinue={onClickContinue}
      onSkip={onSkip}
    />
  )
}
